/**
 * StagingSpecialRequestPopup — native special-request modal (lifted from
 * `common/StagingFlow/StagingSpecialRequestPopup`, rewired onto the module working
 * context + `patchRoom`). Keeps the `if (!activeRoom) return` guard (the room
 * can be removed while the popup is open). Analytics dropped.
 */

import { FC, useCallback, useEffect, useState } from 'react'

import { GRAY_700, GRAY_900 } from 'constants/styling/theme'
import { Stack, Typography } from '@mui/material'
import Modal from 'components/common/Modals/Modal/Modal'
import { MUIButton } from 'components/common/MUIButton'
import { MUIInputField } from 'components/common/MUIInputField'
import { useTranslation } from 'react-i18next'

import styles from 'components/common/StagingFlow/StagingSpecialRequestPopup/StagingSpecialRequestPopup.module.sass'

import { useStagingWorkingState } from '../StagingWorkingState.context'

export const StagingSpecialRequestPopup: FC = () => {
  const { t } = useTranslation(['style_selection_order_step', 'common'])

  const [specialRequest, setSpecialRequest] = useState<string>('')

  const { isSpecialRequestPopupOpen, setIsSpecialRequestPopupOpen, activeRoom, patchRoom } = useStagingWorkingState()

  const onClosePopup = useCallback(() => {
    setIsSpecialRequestPopupOpen(false)
  }, [setIsSpecialRequestPopupOpen])

  const onConfirmRequest = useCallback(() => {
    if (!activeRoom) return
    patchRoom(activeRoom.key, { comment: specialRequest })
    onClosePopup()
  }, [onClosePopup, patchRoom, activeRoom, specialRequest])

  // `activeRoom` can be undefined (empty inventory / room removed) — do not crash.
  const stagingComment = activeRoom?.comment

  useEffect(() => {
    if (isSpecialRequestPopupOpen && stagingComment) {
      setSpecialRequest(stagingComment)
    }
    // Only when the popup opens.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSpecialRequestPopupOpen])

  return (
    <Modal
      isOpen={isSpecialRequestPopupOpen}
      onClickOutside={onClosePopup}
      onClose={onClosePopup}
      title={t('style_selection_order_step:special_request.title')}
      hasHeaderContentDivider
      modalContentClassName={styles.modal}
      afterClosed={() => setSpecialRequest('')}
      footerContent={
        <Stack direction="row" justifyContent="flex-end" paddingTop={2} gap={2}>

          <MUIButton type="secondaryBorder" onClick={onClosePopup}>
            {t('common:Cancel')}
          </MUIButton>

          <MUIButton onClick={onConfirmRequest}>
            {t('style_selection_order_step:special_request.send_request')}
          </MUIButton>

        </Stack>
      }
    >

      <Stack gap="2.4rem" justifyContent="space-between">

        <Stack>

          <Typography variant="text-md" fontWeight="semiBold" color={GRAY_900} marginBottom=".6rem">
            {t('style_selection_order_step:special_request.instruction_title')}
          </Typography>

          <Typography variant="text-md" color={GRAY_700}>
            {t('style_selection_order_step:special_request.instruction_content')}
          </Typography>

        </Stack>

        <Stack marginBottom="3rem">
          <MUIInputField
            isMultiline
            value={specialRequest}
            label={t('style_selection_order_step:special_request.instruction')}
            onChange={(e) => setSpecialRequest(e.target.value)}
          />
        </Stack>

      </Stack>

    </Modal>
  )
}
